import { useRef, useState } from "react";
import { FiMinus } from "@react-icons/all-files/fi/FiMinus";
import { FiPlus } from "@react-icons/all-files/fi/FiPlus";
import styled from "styled-components";

import CounterActionButton, { CounterValue } from "./CounterActionButton";

interface ResourceCounterProps {
  value: number;
  label: string;
  max?: number;
  unit?: string;
  onIncrement: () => void;
  onDecrement: () => void;
  editable?: boolean;
  onDirectInput?: (value: number) => void;
  compact?: boolean;
}

const ResourceCounter = ({
  value,
  label,
  max,
  unit = "개",
  onIncrement,
  onDecrement,
  editable,
  onDirectInput,
  compact,
}: ResourceCounterProps) => {
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleStartEdit = () => {
    if (!editable || !onDirectInput) return;
    setInputValue(String(value));
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const handleConfirm = () => {
    setEditing(false);
    if (!onDirectInput) return;

    const parsed = parseInt(inputValue, 10);
    if (Number.isNaN(parsed) || parsed === value) return;

    const clamped = Math.max(0, Math.min(max ?? 999, parsed));
    onDirectInput(clamped);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setEditing(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filtered = e.target.value.replace(/[^0-9]/g, "");
    const maxLength = max ? String(max).length : 3;
    if (filtered.length > maxLength) return;
    setInputValue(filtered);
  };

  return (
    <Wrapper $compact={compact}>
      <Counter $compact={compact}>
        <CounterActionButton
          disabled={value <= 0}
          onClick={onDecrement}
        >
          <FiMinus />
        </CounterActionButton>
        {editing ? (
          <DirectInput
            ref={inputRef}
            value={inputValue}
            onChange={handleChange}
            onBlur={handleConfirm}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        ) : (
          <CounterValue
            onClick={handleStartEdit}
            $editable={!!editable && !!onDirectInput}
          >
            {max != null ? `${value} / ${max}` : `${value} ${unit}`}
          </CounterValue>
        )}
        <CounterActionButton
          disabled={max != null ? value >= max : false}
          onClick={onIncrement}
        >
          <FiPlus />
        </CounterActionButton>
        <CounterLabel>{label}</CounterLabel>
      </Counter>
    </Wrapper>
  );
};

export default ResourceCounter;

export const Wrapper = styled.div<{ $compact?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: ${({ $compact }) => ($compact ? "0 6px" : "0 10px")};
  column-gap: ${({ $compact }) => ($compact ? "4px" : "6px")};
  min-width: 0;
  font-size: ${({ $compact }) => ($compact ? "12px" : "14px")};
`;

export const Counter = styled.div<{ $compact?: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ $compact }) => ($compact ? "2px" : "5px")};
  margin: ${({ $compact }) => ($compact ? "3px 0" : "5px 0")};
  white-space: nowrap;
  overflow: hidden;

  /* [compact] +/- 버튼과 value 자체를 축소 */
  ${({ $compact }) =>
    $compact &&
    `
      & > button {
        padding: 2px !important;
      }
      & > button svg {
        width: 12px;
        height: 12px;
      }
    `}
`;

const CounterLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: 3px;
`;

const DirectInput = styled.input`
  width: 42px;
  text-align: center;
  font-size: 14px;
  border: 1px solid ${({ theme }) => theme.app.palette.yellow[300]};
  border-radius: 4px;
  padding: 1px 2px;
  outline: none;
  background: ${({ theme }) => theme.app.bg.white};
  color: ${({ theme }) => theme.app.text.dark1};
`;
